import React, { useState } from 'react';
import {
    Box,
    Button,
    CheckBox,
    Form,
    FormField,
    Heading,
    Layer,
    Notification,
    Tip,
} from 'grommet';
import { Close, Edit } from 'grommet-icons';
import { useRevalidator } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ApiSearch, SchoolService } from '@enrich/shared-lib';

export const EditSchoolButton = ({ school }) => {
    const [showModal, setShowModal] = useState(false);
    const [notification, setNotification] = useState(null);

    return (
        <>
            <Button
                secondary
                size="medium"
                label={
                    <span>
                        <Edit size="medium" color="enrich-blue" />
                    </span>
                }
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <EditSchoolModal
                    school={school}
                    setShowModal={setShowModal}
                    setNotification={setNotification}
                />
            )}
            {notification && (
                <Notification
                    toast
                    status={notification.status}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
        </>
    );
};

const EditSchoolModal = ({ school, setShowModal, setNotification }) => {
    const [value, setValue] = useState({
        isReportClient: !!school.attributes.isReportClient,
    });
    const initialLocationId = school.attributes.location?.data?.id ?? null;
    const [location, setLocation] = useState(
        school.attributes.location?.data?.attributes
            ? {
                  id: school.attributes.location.data.id,
                  ...school.attributes.location.data.attributes,
              }
            : null,
    );
    const [buttonBusy, setButtonBusy] = useState(false);
    const revalidator = useRevalidator();

    const handleSubmit = async (submitted) => {
        setButtonBusy(true);
        try {
            // Only send `location` when the user actually changed it. The
            // modal seeds it from the loader's populated relation, so an
            // unpopulated relation would otherwise be overwritten with null
            // by an edit that never touched the field.
            const nextLocationId = location?.id ?? null;
            const payload = { isReportClient: submitted.isReportClient };
            if (nextLocationId !== initialLocationId) {
                payload.location = nextLocationId;
            }
            await new SchoolService().update(school.id, payload);
            setNotification({
                status: 'normal',
                title: 'Success',
                message: 'School updated.',
            });
            setShowModal(false);
            revalidator.revalidate();
        } catch (error) {
            console.log(error);
            const message =
                error.response?.data?.error?.message ||
                error.message ||
                'Failed to update school.';
            setNotification({
                status: 'critical',
                title: 'Update failed',
                message,
            });
            setButtonBusy(false);
        }
    };

    const tipContent = value.isReportClient
        ? 'Turn off reporting'
        : 'Turn on reporting';

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                gap="30px"
                width="medium">
                <Box fill="horizontal" direction="row" justify="between">
                    <Box>
                        <Heading level="3" margin={{ vertical: 'none' }}>
                            Edit School
                        </Heading>
                    </Box>
                    <Box
                        align="end"
                        round={true}
                        onClick={() => setShowModal(false)}
                        cursor="pointer">
                        <Close color="enrich-red" />
                    </Box>
                </Box>
                <Box>
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onSubmit={({ value: submitted }) =>
                            handleSubmit(submitted)
                        }>
                        <FormField
                            label="Reporting Status"
                            name="isReportClient">
                            <Tip
                                content={tipContent}
                                dropProps={{
                                    align: { bottom: 'top' },
                                    margin: 'xsmall',
                                }}>
                                <Box pad={{ left: 'small', vertical: 'small' }}>
                                    <CheckBox
                                        toggle
                                        a11yTitle="Toggle reporting status"
                                        name="isReportClient"
                                        checked={!!value.isReportClient}
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                isReportClient:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </Box>
                            </Tip>
                        </FormField>

                        <FormField label="Closest Location" name="location">
                            <Box pad={{ vertical: 'small' }}>
                                <ApiSearch
                                    contentType="locations"
                                    clearable={true}
                                    placeholder="Select a location"
                                    defaultValue={location ? [location] : []}
                                    updateFormData={(val) =>
                                        setLocation(val[0] ?? null)
                                    }
                                />
                            </Box>
                        </FormField>

                        <Box>
                            <Button
                                primary
                                type="submit"
                                size="medium"
                                fill="horizontal"
                                busy={buttonBusy}
                                label="Submit"
                                alignSelf="center"
                            />
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

EditSchoolButton.propTypes = {
    school: PropTypes.object,
};

EditSchoolModal.propTypes = {
    school: PropTypes.object,
    setShowModal: PropTypes.func,
    setNotification: PropTypes.func,
};
